import { useCallback, useState, useEffect } from 'react';
import analyticsService, { fetchAnalyticsData, AnalyticsData } from '../services/analyticsService';

interface UseAnalyticsReturn {
  trackPageView: (pageName: string, additionalParams?: Record<string, string | number | boolean | undefined>) => void;
  trackSignUp: (method?: string) => void;
  trackLogin: (method?: string) => void;
  trackVisaApplicationStart: (visaType: string) => void;
  trackVisaApplicationStep: (visaType: string, step: string) => void;
  trackChatMessage: (visaType: string, messageType?: 'user' | 'assistant') => void;
  trackDocumentUpload: (visaType: string, documentType: string) => void;
  trackSubscriptionEvent: (eventType: 'view_plans' | 'start_trial' | 'subscribe' | 'cancel', plan?: string) => void;
  trackCustomEvent: (eventName: string, parameters?: Record<string, string | number | boolean | undefined>) => void;
  trackError: (error: Error, context?: string) => void;
  trackFeatureUsage: (feature: string, visaType?: string) => void;
  setUserProperties: (properties: Record<string, string | number | boolean | undefined>) => void;
  analyticsData: AnalyticsData | null;
  loading: boolean;
  error: string | null;
  logEvent: (eventName: string, eventParams?: Record<string, string | number | boolean | undefined>) => void;
  logCustomEvent: (eventName: string, eventParams?: Record<string, string | number | boolean | undefined>) => void;
}

export const useAnalytics = (): UseAnalyticsReturn => {
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadAnalyticsData = async () => {
      try {
        setLoading(true);
        const data = await fetchAnalyticsData();
        setAnalyticsData(data);
      } catch (err) {
        setError('Failed to load analytics data');
        console.error('Error loading analytics data:', err);
      } finally {
        setLoading(false);
      }
    };

    loadAnalyticsData();
  }, []);

  const trackPageView = useCallback((pageName: string, additionalParams?: Record<string, string | number | boolean | undefined>) => {
    analyticsService.trackPageView(pageName, additionalParams);
  }, []);

  const trackSignUp = useCallback((method = 'email') => {
    analyticsService.trackSignUp(method);
  }, []);

  const trackLogin = useCallback((method = 'email') => {
    analyticsService.trackLogin(method);
  }, []);

  const trackVisaApplicationStart = useCallback((visaType: string) => {
    analyticsService.trackVisaApplicationStart(visaType);
  }, []);

  const trackVisaApplicationStep = useCallback((visaType: string, step: string) => {
    analyticsService.trackVisaApplicationStep(visaType, step);
  }, []);

  const trackChatMessage = useCallback((visaType: string, messageType: 'user' | 'assistant' = 'user') => {
    analyticsService.trackChatMessage(visaType, messageType);
  }, []);

  const trackDocumentUpload = useCallback((visaType: string, documentType: string) => {
    analyticsService.trackDocumentUpload(visaType, documentType);
  }, []);

  const trackSubscriptionEvent = useCallback((eventType: 'view_plans' | 'start_trial' | 'subscribe' | 'cancel', plan?: string) => {
    analyticsService.trackSubscriptionEvent(eventType, plan);
  }, []);

  const trackCustomEvent = useCallback((eventName: string, parameters?: Record<string, string | number | boolean | undefined>) => {
    analyticsService.trackCustomEvent(eventName, parameters);
  }, []);

  const trackError = useCallback((error: Error, context?: string) => {
    analyticsService.trackError(error, context);
  }, []);

  const trackFeatureUsage = useCallback((feature: string, visaType?: string) => {
    analyticsService.trackFeatureUsage(feature, visaType);
  }, []);

  const setUserProperties = useCallback((properties: Record<string, string | number | boolean | undefined>) => {
    analyticsService.setUserProperties(properties);
  }, []);

  const logEvent = useCallback((eventName: string, eventParams?: Record<string, string | number | boolean | undefined>) => {
    analyticsService.trackCustomEvent(eventName, eventParams);
  }, []);

  const logCustomEvent = useCallback((eventName: string, eventParams?: Record<string, string | number | boolean | undefined>) => {
    analyticsService.trackCustomEvent(eventName, eventParams);
  }, []);

  return {
    trackPageView,
    trackSignUp,
    trackLogin,
    trackVisaApplicationStart,
    trackVisaApplicationStep,
    trackChatMessage,
    trackDocumentUpload,
    trackSubscriptionEvent,
    trackCustomEvent,
    trackError,
    trackFeatureUsage,
    setUserProperties,
    analyticsData,
    loading,
    error,
    logEvent,
    logCustomEvent,
  };
}; 